// Activity's sources (SCR-592, ADR-114): the `ActivitySource` port and its three adapters. Each
// reads one store for a window and turns its answer into `ActivityRow`s; the screen merges them
// (model.ts). A source throws when its read fails; the screen turns that into a note naming it.
//   orgAuditSource    API-588, the org's audit, staff already masked by the API (IA-5);
//   guardrailSource   the usage events with the guardrail classes, the senders named from the
//                     org's users (the Guardrails tab's former Triggered card, R-10);
//   proxyAuditSource  the proxy's own sign-in and key audit (`GET /api/admin/v1/audit?month=`),
//                     which only the proxy shell has; it injects the client.
import type { ApiClient } from "../../api/client";
import type { components } from "../../api/schema";
import { addMonths } from "../../primitives/months";
import { personName } from "../../primitives/person-name";
import { PROXY_BASE, type ProxyClient } from "../people/source";
import {
  actorText,
  auditActionText,
  changedPaths,
  defaultFilters,
  kindOfAudit,
  kindOfProxyAction,
  NO_TARGET,
  outcomeLabel,
  proxyActionText,
  wantsKind,
  windowOf,
  type ActivityFilters,
  type ActivityRow,
  type ActivityWindow,
  type ActorKind,
  type GuardrailOutcome,
} from "./model";

type OrgAuditEvent = components["schemas"]["OrgAuditEvent"];
type OrgAuditKind = components["schemas"]["OrgAuditKind"];
type UsageEventRecord = components["schemas"]["UsageEventRecord"];
type ErrorClass = components["schemas"]["ErrorClass"];
type User = components["schemas"]["User"];

/** One page of a source: its rows and the cursor of the next page, null at the end. */
export interface ActivityPage {
  items: ActivityRow[];
  nextCursor: string | null;
}

/** A store the timeline reads. `label` names it in the note when it fails ("guardrail interventions"). */
export interface ActivitySource {
  readonly label: string;
  load(window: ActivityWindow, filters: Pick<ActivityFilters, "kinds">, cursor: string | null): Promise<ActivityPage>;
}

const EMPTY: ActivityPage = { items: [], nextCursor: null };
export const PAGE_LIMIT = 100;
/** One page of the org's users names every sender the card ever named. */
const USERS_LIMIT = 500;

// ------------------------------------------------------------------------- API-588, org audit

const AUDIT_KINDS: readonly OrgAuditKind[] = ["settings", "quotas", "people", "keys", "sign_in", "notifications", "assistant"];

/** API-588's row as the timeline shows it. */
export function orgAuditRow(event: OrgAuditEvent): ActivityRow {
  const detail = event.detail ?? null;
  const paths = changedPaths(detail);
  // A support command's reason is written for the customer (REQ-176); the API sends no other staff reason.
  const reason = event.action.startsWith("support.") && typeof detail?.["reason"] === "string" ? detail["reason"] : null;
  return {
    key: `audit:${event.id}`,
    source: "audit",
    at: event.occurredAt,
    kind: kindOfAudit(event.kind),
    actorKind: event.actorKind,
    ...actorText(event.actorKind, event.actorName, event.actorEmail),
    what: auditActionText(event.action, detail),
    target: event.targetName?.trim() || NO_TARGET,
    facts: [...(paths ? [{ label: "Changed", value: paths }] : []), ...(reason ? [{ label: "Reason", value: reason }] : [])],
    guardrail: null,
  };
}

export function orgAuditSource(client: ApiClient, orgId: string): ActivitySource {
  return {
    label: "the organisation's audit",
    async load(window, filters, cursor) {
      const kind = AUDIT_KINDS.filter((k) => wantsKind(filters, k));
      if (kind.length === 0) return EMPTY;
      const query = { ...window, limit: PAGE_LIMIT, ...(filters.kinds.length > 0 ? { kind } : {}), ...(cursor ? { cursor } : {}) };
      const page = await client.get("/v1/orgs/{orgId}/audit-events", { path: { orgId }, query });
      return { items: page.items.map(orgAuditRow), nextCursor: page.nextCursor };
    },
  };
}

// ------------------------------------------------------------------ guardrail interventions

/** The classes a guardrail sets; the events filter asks for exactly these. */
export const GUARDRAIL_ERROR_CLASSES = ["guardrail_blocked", "guardrail_intervened", "guardrail_unavailable"] as const satisfies readonly ErrorClass[];

export function isGuardrailClass(errorClass: ErrorClass): boolean {
  return (GUARDRAIL_ERROR_CLASSES as readonly ErrorClass[]).includes(errorClass);
}

/**
 * What the guardrail did. The class says blocked or unavailable; an intervention is on the input
 * or the output as the record's action says, and on the output (the answer replaced, the class's
 * contract meaning) when no record says otherwise.
 */
export function guardrailOutcome(event: Pick<UsageEventRecord, "errorClass" | "guardrail">): GuardrailOutcome {
  if (event.errorClass === "guardrail_unavailable") return "unavailable";
  if (event.errorClass === "guardrail_blocked") return "blocked";
  return event.guardrail?.action === "intervened_input" ? "intervened_input" : "intervened_output";
}

const OUTCOME_SENTENCE: Readonly<Record<GuardrailOutcome, string>> = {
  blocked: "The guardrail blocked a request",
  intervened_input: "The guardrail intervened on a prompt",
  intervened_output: "The guardrail replaced an answer",
  unavailable: "The guardrail could not be applied",
};

/** Why an intervention names no policies: the proxy reports them only from the guardrail trace. */
export const NO_TRACE = "No trace (the installation's guardrails.trace setting is off)";

/** The events query for the window: only guardrail outcomes, one page. */
export function guardrailEventsQuery(window: ActivityWindow, cursor: string | null) {
  return { ...window, errorClass: [...GUARDRAIL_ERROR_CLASSES], limit: PAGE_LIMIT, ...(cursor ? { cursor } : {}) };
}

/** Where a stored prompt opens, when the shell offers Prompt logs to this principal. */
export type PromptHref = (event: { eventId: string; ts: string }) => string | null;

/** One intervention as a row; the sender by name from `users`, never their id. */
export function guardrailRow(event: UsageEventRecord, users: ReadonlyMap<string, User>, promptHref: PromptHref | null): ActivityRow {
  const user = users.get(event.userId);
  const outcome = guardrailOutcome(event);
  const policies = event.guardrail?.policies ?? [];
  const model = event.bedrockModelId ?? event.requestedModel;
  const guardrail = event.guardrail ? `${event.guardrail.id}@${event.guardrail.version}` : null;
  const untraced = outcome !== "unavailable" && policies.length === 0;
  return {
    key: `guardrail:${event.eventId}`,
    source: "guardrail",
    at: event.ts,
    kind: "guardrail",
    actorKind: "person",
    // personName: the display name, else the email, else an AWS principal's name, else "Unknown
    // user"; never the external id as it is (IA-1).
    ...actorText("person", user ? personName(user) : null, user?.email),
    what: OUTCOME_SENTENCE[outcome],
    target: model,
    facts: [
      { label: "Outcome", value: outcomeLabel(outcome) },
      { label: "Model", value: model },
      { label: "Tool", value: event.platform },
      { label: "Guardrail", value: guardrail ?? "—" },
      { label: "Policies", value: untraced ? NO_TRACE : policies.join(", ") || "—" },
    ],
    guardrail: {
      outcome,
      model,
      guardrail,
      policies,
      untraced,
      promptHref: promptHref && event.bodyStatus === "stored" ? promptHref(event) : null,
    },
  };
}

export interface GuardrailSourceOptions {
  /** The stored prompt's link, for a prompt reader; absent, no row links a prompt. */
  promptHref?: PromptHref | undefined;
}

/**
 * Reads one page of the window's guardrail events and the users to name them (users best-effort).
 * The users are read once per source, which a shell builds once per mount, and shared by every
 * page; a failed read is tried again on the next page.
 */
export function guardrailSource(client: ApiClient, orgId: string, options: GuardrailSourceOptions = {}): ActivitySource {
  const path = { orgId };
  let users: Promise<ReadonlyMap<string, User>> | null = null;
  const usersById = (): Promise<ReadonlyMap<string, User>> => {
    users ??= client
      .get("/v1/orgs/{orgId}/users", { path, query: { limit: USERS_LIMIT } })
      .then((list) => new Map(list.items.map((user) => [user.id, user])))
      .catch(() => {
        users = null;
        return new Map<string, User>();
      });
    return users;
  };
  return {
    label: "guardrail interventions",
    async load(window, filters, cursor) {
      if (!wantsKind(filters, "guardrail")) return EMPTY;
      const [page, byId] = await Promise.all([client.get("/v1/orgs/{orgId}/usage/events", { path, query: guardrailEventsQuery(window, cursor) }), usersById()]);
      const items = page.items.filter((event) => isGuardrailClass(event.errorClass)).map((event) => guardrailRow(event, byId, options.promptHref ?? null));
      return { items, nextCursor: page.nextCursor };
    },
  };
}

/**
 * The Activity tab's badge (R-10): how many guardrail rows the Log's default window opens with
 * (its first page, as the Triggered card counted), 0 when the read fails. A count names nobody,
 * so it reads the events alone, not the users.
 */
export async function guardrailCount(client: ApiClient, orgId: string, now: Date): Promise<number> {
  try {
    const page = await client.get("/v1/orgs/{orgId}/usage/events", { path: { orgId }, query: guardrailEventsQuery(windowOf(defaultFilters(now)), null) });
    return page.items.filter((event) => isGuardrailClass(event.errorClass)).length;
  } catch {
    return 0;
  }
}

// --------------------------------------------------------------- the proxy's own audit (ENT-486)

/** One item of `GET /api/admin/v1/audit` (web/server/internal/adminid/audit.go). */
export interface ProxyAuditItem {
  id: string;
  at: string;
  action: string;
  /** self, system, support or admin:<userId>. */
  actor: string;
  userId?: string;
  keyId?: string;
  inviteId?: string;
  method?: string;
  reason?: string;
  commandReason?: string;
  reasonCode?: string;
  outcome?: string;
}

interface ProxyPerson {
  name: string | null;
  email: string | null;
}

const ADMIN_ACTOR = "admin:";

function proxyActor(item: ProxyAuditItem): { kind: ActorKind; userId: string | null } {
  if (item.actor === "support") return { kind: "staff", userId: null };
  if (item.actor === "self") return { kind: "person", userId: item.userId ?? null };
  if (item.actor.startsWith(ADMIN_ACTOR)) return { kind: "person", userId: item.actor.slice(ADMIN_ACTOR.length) };
  return { kind: "system", userId: null };
}

/** A proxy-local item as a row: people by name from the proxy's own list, never their id. */
export function proxyAuditRow(item: ProxyAuditItem, people: ReadonlyMap<string, ProxyPerson>): ActivityRow {
  const actor = proxyActor(item);
  const who = actor.userId ? people.get(actor.userId) : undefined;
  const subject = item.userId ? people.get(item.userId) : undefined;
  const facts = [
    ...(item.method ? [{ label: "Method", value: item.method }] : []),
    ...(item.reason ? [{ label: "Reason", value: item.reason }] : []),
    ...(item.commandReason ? [{ label: "Support's reason", value: item.commandReason }] : []),
    ...(item.outcome ? [{ label: "Outcome", value: item.outcome.replace(/_/g, " ") }] : []),
  ];
  return {
    key: `proxy:${item.id}`,
    source: "proxy",
    at: item.at,
    kind: kindOfProxyAction(item.action, item.keyId),
    actorKind: actor.kind,
    ...actorText(actor.kind, who?.name, who?.email),
    what: proxyActionText(item.action),
    target: subject ? actorText("person", subject.name, subject.email).who : NO_TARGET,
    facts,
    guardrail: null,
  };
}

async function getJson(client: ProxyClient, path: string): Promise<Record<string, unknown>> {
  const response = await client.fetch(PROXY_BASE + path, { method: "GET", headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(`${path}: ${response.status}`);
  return (await response.json()) as Record<string, unknown>;
}

/** The proxy's people by id, best-effort: one page names the actors of a month. */
async function proxyPeople(client: ProxyClient): Promise<Map<string, ProxyPerson>> {
  try {
    const body = await getJson(client, "/people");
    const list = Array.isArray(body["people"]) ? (body["people"] as Record<string, unknown>[]) : [];
    const text = (value: unknown) => (typeof value === "string" && value !== "" ? value : null);
    return new Map(list.map((p) => [String(p["userId"]), { name: text(p["displayName"]), email: text(p["email"]) }]));
  } catch {
    return new Map();
  }
}

/** The months (yyyy-mm) a window touches, newest first; `to` is exclusive. */
export function monthsOf(window: ActivityWindow): string[] {
  const first = window.from.slice(0, 7);
  const months: string[] = [];
  for (let month = new Date(Date.parse(window.to) - 1).toISOString().slice(0, 7); month >= first; month = addMonths(month, -1)) months.push(month);
  return months;
}

/** The cursor across months: the month being read and the store's cursor inside it. */
function splitCursor(cursor: string | null, months: readonly string[]): { month: string | undefined; inner: string } {
  if (!cursor) return { month: months[0], inner: "" };
  const bar = cursor.indexOf("|");
  return bar < 0 ? { month: cursor, inner: "" } : { month: cursor.slice(0, bar), inner: cursor.slice(bar + 1) };
}

/**
 * The proxy's own audit: one month's page per load, newest month first, the next month's cursor
 * when one is done. Its kinds are People, Keys, Sign-in and Settings.
 */
export function proxyAuditSource(client: ProxyClient): ActivitySource {
  return {
    label: "this proxy's sign-in and key audit",
    async load(window, filters, cursor) {
      const months = monthsOf(window);
      const { month, inner } = splitCursor(cursor, months);
      if (month === undefined || !(["people", "keys", "sign_in", "settings"] as const).some((kind) => wantsKind(filters, kind))) return EMPTY;
      const query = new URLSearchParams({ month, ...(inner ? { cursor: inner } : {}) });
      const [body, people] = await Promise.all([getJson(client, `/audit?${query.toString()}`), proxyPeople(client)]);
      const items = (Array.isArray(body["items"]) ? (body["items"] as ProxyAuditItem[]) : [])
        .filter((item) => item.at >= window.from && item.at < window.to)
        .map((item) => proxyAuditRow(item, people))
        .filter((row) => wantsKind(filters, row.kind));
      const next = typeof body["cursor"] === "string" && body["cursor"] !== "" ? `${month}|${body["cursor"]}` : months[months.indexOf(month) + 1] ?? null;
      return { items, nextCursor: next };
    },
  };
}
